"use client"

import { cn, formatDate, formatDisplayPrice } from '@/utils/index'
import { Check, Calendar } from "lucide-react"
import { Category } from "@/app/types"
import Rating from "../Rating/rating"

interface ProductStatusProps {
  condition: string
  updatedAt: string | Date
  averageRating: number
  reviewsCount: number
  translations: Record<string, any>
  viewCount?: number
}

interface ProductHeaderProps extends ProductStatusProps {
  title: string
  className?: string
  currency?: string
  categories: Category[]
  price: number | string | null
  translations: Record<string, any>
  productId: string
  editorView?: boolean
  initialViewCount?: number
}

const ProductStatus = ({ condition, updatedAt, averageRating, reviewsCount, translations, viewCount }: ProductStatusProps) => {
  let conditionLabel = ""
  let conditionClass = ""

  switch (condition) {
    case "NEW":
      conditionLabel = translations.new
      conditionClass = "bg-green-100 text-green-800"
      break
    case "LIKE_NEW":
      conditionLabel = translations.likeNew
      conditionClass = "bg-blue-100 text-blue-800"
      break
    case "SLIGHTLY_USED":
      conditionLabel = translations.slightlyUsed
      conditionClass = "bg-amber-100 text-amber-800"
      break
    case "USED":
      conditionLabel = translations.used
      conditionClass = "bg-orange-100 text-orange-800"
      break
    default:
      conditionLabel = condition
      conditionClass = "bg-gray-100 text-gray-800"
  }

  return (
    <div className="my-2 flex flex-col">
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${conditionClass}`}>{conditionLabel}</span>
        <div className="h-4 w-px bg-gray-300"></div>
        <div className="flex items-center gap-0.5">
          <Rating stars={averageRating} />
          <span className="ml-1 text-xs font-medium text-gray-600 dark:text-gray-300">
            {averageRating} ({reviewsCount} {translations.reviews})
          </span>
        </div>
      </div>

      <div className="my-4 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <Calendar className="h-4 w-4 text-green-600" />
          <span>{`${translations.listedOn} ${formatDate(updatedAt)}`}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <Check className="h-4 w-4 text-blue-600" />
          <span>{translations.stock}</span>
        </div>
      </div>
    </div>
  )
}

export const ProductHeader = ({
  title,
  categories,
  price,
  condition,
  updatedAt,
  averageRating,
  reviewsCount,
  className,
  translations,
}: ProductHeaderProps) => {
  return (
    <div className={cn("gap-6", className)}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="w-fit rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
            {categories[0]?.name}
          </span>
        </div>
      </div>
      
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-300 sm:text-4xl capitalize">
        {title}
      </h1>

      <ProductStatus
        condition={condition}
        updatedAt={updatedAt}
        averageRating={averageRating}
        reviewsCount={reviewsCount}
        translations={translations}
      />

      <p className="text-2xl font-semibold">
        {formatDisplayPrice(price)}
      </p>
    </div>
  )
}
